"use client";

import { AlertCircle, Bookmark, Minus, Plus, ShieldCheck, ShoppingBag, Trash2, X } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useCartStore } from "@/components/providers/cart-store";
import { useWishlistStore } from "@/components/providers/wishlist-store";
import { calculateShipping } from "@/lib/delivery";
import { formatPrice } from "@/lib/utils";

function CartThumb({ src, alt }: { src?: string; alt: string }) {
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
        <ShoppingBag className="h-7 w-7" />
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      fill
      sizes="(max-width: 640px) 80px, 112px"
      className="object-contain p-1"
      onError={() => setError(true)}
    />
  );
}

export function CartView() {
  const [mounted, setMounted] = useState(false);
  const items = useCartStore((s) => s.items);
  const savedItems = useCartStore((s) => s.savedItems);
  const subtotal = useCartStore((s) => s.subtotal());
  const couponCode = useCartStore((s) => s.couponCode);
  const couponDiscount = useCartStore((s) => s.couponDiscount);
  const clearCoupon = useCartStore((s) => s.clearCoupon);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const saveForLater = useCartStore((s) => s.saveForLater);
  const moveToCart = useCartStore((s) => s.moveToCart);
  const removeSavedItem = useCartStore((s) => s.removeSavedItem);
  const moveToWishlist = useCartStore((s) => s.moveToWishlist);
  const addToWishlist = useWishlistStore((s) => s.add);

  useEffect(() => setMounted(true), []);

  const validItems = items.filter((item) => item?.product?.id);
  const validSavedItems = savedItems.filter((item) => item?.product?.id);

  const shipping = calculateShipping(subtotal, true);
  const total = Math.max(0, subtotal - couponDiscount) + shipping;

  const hasOutOfStockItems = validItems.some(({ product, selectedVariant }) => {
    const stock = selectedVariant?.stock !== undefined ? selectedVariant.stock : product.stock;
    return stock <= 0;
  });

  if (!mounted) {
    return (
      <div className="amazon-section py-16 text-center">
        <div className="mx-auto h-8 w-48 animate-pulse rounded bg-slate-200 dark:bg-white/10" />
      </div>
    );
  }

  if (validItems.length === 0 && validSavedItems.length === 0) {
    return (
      <div className="mx-auto max-w-xl my-12 rounded-2xl border border-slate-200/80 bg-white p-8 text-center shadow-xs dark:border-white/10 dark:bg-slate-900">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-sky-50 text-[#087DB5] dark:bg-slate-800">
          <ShoppingBag className="h-10 w-10" />
        </div>
        <h1 className="mt-5 text-2xl sm:text-3xl font-bold text-[#073B70] dark:text-white">Your MOVA Cart is empty</h1>
        <p className="mt-2 text-sm text-slate-500">Explore our quality catalog and get exclusive online discounts.</p>
        <Link
          href="/search"
          className="mt-6 inline-flex h-11 items-center justify-center rounded-xl bg-gradient-to-r from-[#087DB5] to-[#073B70] px-6 text-sm font-bold text-white shadow-sm transition hover:brightness-110"
        >
          Continue shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] grid gap-6 px-3 py-6 sm:px-4 lg:grid-cols-[1fr_340px] pb-24 sm:pb-8">
      <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs dark:border-white/10 dark:bg-slate-900 sm:p-6">
        <div className="flex items-baseline justify-between border-b border-slate-100 pb-4 dark:border-white/10">
          <h1 className="text-2xl font-medium text-slate-950 dark:text-white">Shopping Cart</h1>
          <span className="hidden text-sm text-slate-500 sm:inline">Price</span>
        </div>

        {validItems.length === 0 ? (
          <div className="py-12 text-center">
            <ShoppingBag className="mx-auto h-12 w-12 text-[#087DB5]/50" />
            <h2 className="mt-3 text-lg font-medium text-slate-950 dark:text-white">Your shopping cart is currently empty</h2>
            <p className="mt-1 text-sm text-slate-500">Items you saved for later are shown below.</p>
            <Link
              href="/search"
              className="mt-5 inline-flex h-10 items-center justify-center rounded-xl bg-gradient-to-r from-[#087DB5] to-[#073B70] px-5 text-sm font-bold text-white shadow-sm hover:brightness-110"
            >
              Continue shopping
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-white/10">
            {validItems.map(({ product, quantity, selectedVariant }) => {
              const itemKey = `${product.id}-${selectedVariant?.id || "base"}`;
              const itemImage = selectedVariant?.image || product.images?.[0];
              const itemUnitPrice = selectedVariant?.price ?? product.price;
              const availableStock = selectedVariant?.stock !== undefined ? selectedVariant.stock : product.stock;
              const isOutOfStock = availableStock <= 0;
              const maxAllowed = Math.min(20, Math.max(1, availableStock));

              const productUrl = selectedVariant
                ? `/products/${product.slug || product.id}?variant=${encodeURIComponent(selectedVariant.id)}`
                : `/products/${product.slug || product.id}`;

              return (
                <article key={itemKey} className="flex flex-col gap-3 py-4 sm:flex-row sm:justify-between sm:gap-4 sm:py-5">
                  <div className="flex gap-3 sm:gap-4">
                    <Link
                      href={productUrl}
                      className="relative h-20 w-20 sm:h-28 sm:w-28 shrink-0 overflow-hidden rounded-xl border border-slate-100 bg-white dark:border-white/5"
                    >
                      <CartThumb src={itemImage} alt={product.title} />
                    </Link>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <Link href={productUrl} className="amazon-link line-clamp-2 text-sm sm:text-base font-semibold leading-snug">
                          {product.title}
                        </Link>
                        <div className="text-right text-base font-bold text-slate-950 dark:text-white shrink-0 sm:hidden">
                          {formatPrice(itemUnitPrice * quantity)}
                        </div>
                      </div>
                      {selectedVariant && (
                        <div className="mt-1 space-y-0.5 text-xs text-slate-600 dark:text-slate-400">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                              {selectedVariant.title ? "Variant:" : "Color:"}
                            </span>
                            {selectedVariant.colorCode && (
                              <span
                                className="inline-block h-3 w-3 rounded-full border border-black/20"
                                style={{ backgroundColor: selectedVariant.colorCode }}
                              />
                            )}
                            <span className="font-medium text-slate-900 dark:text-white">
                              {selectedVariant.title || selectedVariant.color}
                            </span>
                            <span className="text-slate-400">({formatPrice(selectedVariant.price)} each)</span>
                          </div>
                          {selectedVariant.sku && (
                            <p className="font-mono text-[11px] text-slate-400">SKU: {selectedVariant.sku}</p>
                          )}
                          {selectedVariant.attributes && Object.keys(selectedVariant.attributes).length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-0.5">
                              {Object.entries(selectedVariant.attributes).map(([k, v]) => (
                                <span
                                  key={k}
                                  className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                >
                                  <span className="capitalize">{k}:</span> <strong>{v}</strong>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {!selectedVariant && quantity > 1 && (
                        <p className="mt-0.5 text-xs text-slate-500">
                          {formatPrice(product.price)} each
                        </p>
                      )}

                      {/* Stock status indicator */}
                      {isOutOfStock ? (
                        <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400">
                          Out of stock
                        </p>
                      ) : availableStock <= 5 ? (
                        <p className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                          Only {availableStock} left in stock - order soon
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-amazon-green">In stock</p>
                      )}

                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <div className="inline-flex h-9 sm:h-8 items-center rounded-lg border border-slate-300 dark:border-white/10 bg-slate-50/50 dark:bg-slate-800/50 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => updateQuantity(product.id, quantity - 1, selectedVariant?.id)}
                            disabled={quantity <= 1}
                            className="h-full w-9 sm:w-8 flex items-center justify-center transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-white/10 touch-manipulation"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="min-w-8 text-center text-sm font-bold">{quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(product.id, quantity + 1, selectedVariant?.id)}
                            disabled={quantity >= maxAllowed || isOutOfStock}
                            className="h-full w-9 sm:w-8 flex items-center justify-center transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-white/10 touch-manipulation"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => saveForLater(product.id, selectedVariant?.id)}
                          className="text-xs text-amazon-teal hover:underline"
                        >
                          Save for later
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            addToWishlist(product);
                            moveToWishlist(product.id, selectedVariant?.id);
                            toast.success("Moved to wishlist");
                          }}
                          className="text-xs text-amazon-teal hover:underline"
                        >
                          Move to wishlist
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(product.id, selectedVariant?.id)}
                          className="inline-flex items-center gap-1 text-xs text-amazon-teal hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="hidden sm:block text-right text-lg font-bold text-slate-950 dark:text-white">
                    {formatPrice(itemUnitPrice * quantity)}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {validSavedItems.length > 0 && (
          <div className="mt-8 border-t border-slate-200 pt-6 dark:border-white/10">
            <h2 className="flex items-center gap-2 font-bold text-slate-950 dark:text-white">
              <Bookmark className="h-4 w-4" />
              Saved for later ({validSavedItems.length})
            </h2>
            <div className="divide-y divide-slate-100 dark:divide-white/5">
              {validSavedItems.map(({ product, quantity, selectedVariant }) => {
                const savedKey = `${product.id}-${selectedVariant?.id || "base"}`;
                const savedImage = selectedVariant?.image || product.images?.[0];
                const savedPrice = selectedVariant?.price ?? product.price;
                const savedLink = selectedVariant
                  ? `/products/${product.slug || product.id}?variant=${encodeURIComponent(selectedVariant.id)}`
                  : `/products/${product.slug || product.id}`;

                return (
                  <div key={savedKey} className="flex gap-4 py-4">
                    <Link
                      href={savedLink}
                      className="relative h-20 w-20 shrink-0 overflow-hidden rounded border border-slate-100 bg-slate-50 dark:border-white/5"
                    >
                      <CartThumb src={savedImage} alt={product.title} />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link href={savedLink} className="line-clamp-2 text-sm font-medium hover:text-amazon-orange">
                        {product.title}
                      </Link>
                      {selectedVariant && (
                        <p className="mt-0.5 text-xs text-slate-500">
                          {selectedVariant.title || selectedVariant.color}
                          {selectedVariant.sku ? ` · SKU: ${selectedVariant.sku}` : ""}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => moveToCart(product.id, selectedVariant?.id)}
                          className="text-xs text-amazon-teal hover:underline font-medium"
                        >
                          Move to cart
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSavedItem(product.id, selectedVariant?.id)}
                          className="text-xs text-amazon-teal hover:text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <span className="font-bold">{formatPrice(savedPrice * quantity)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <aside className="h-fit rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs dark:border-white/10 dark:bg-slate-900 lg:sticky lg:top-20">
        <h2 className="text-base text-slate-700 dark:text-slate-300">
          Subtotal ({validItems.reduce((n, i) => n + i.quantity, 0)} items):{" "}
          <span className="block text-2xl font-bold text-slate-950 dark:text-white mt-1">
            {formatPrice(subtotal)}
          </span>
        </h2>

        <div className="mt-4 space-y-2.5 text-sm">
          {couponDiscount > 0 && (
            <div className="flex items-center justify-between font-semibold text-[#00B7B0]">
              <div className="flex items-center gap-1.5">
                <span>Coupon {couponCode ? `(${couponCode})` : ""}</span>
                <button
                  type="button"
                  onClick={() => {
                    clearCoupon();
                    toast.success("Coupon removed");
                  }}
                  title="Remove coupon"
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <strong>-{formatPrice(couponDiscount)}</strong>
            </div>
          )}
          <div className="flex justify-between text-slate-600 dark:text-slate-400">
            <span>Shipping</span>
            <strong className="text-slate-900 dark:text-white">{shipping === 0 ? "FREE" : formatPrice(shipping)}</strong>
          </div>
          <div className="border-t border-slate-100 pt-3 text-base font-bold dark:border-white/10">
            <div className="flex justify-between items-baseline">
              <span className="text-slate-950 dark:text-white">Order Total</span>
              <span className="text-xl font-bold text-slate-950 dark:text-white">{formatPrice(total)}</span>
            </div>
          </div>
        </div>

        {validItems.length === 0 ? (
          <Link
            href="/search"
            className="mt-5 flex h-12 w-full items-center justify-center rounded-xl bg-[#FF8A00] text-sm font-bold text-white shadow-sm transition hover:bg-[#e67c00]"
          >
            Continue shopping
          </Link>
        ) : hasOutOfStockItems ? (
          <div className="mt-5 space-y-2">
            <div className="flex items-start gap-2 rounded-xl bg-red-50 p-3 text-xs font-medium text-red-700 dark:bg-red-950/30 dark:text-red-400">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-600 mt-0.5" />
              <span>Please remove or save out-of-stock items for later before proceeding.</span>
            </div>
            <button
              type="button"
              disabled
              className="flex h-12 w-full cursor-not-allowed items-center justify-center rounded-xl bg-slate-200 font-bold text-slate-400 dark:bg-white/10 dark:text-slate-500"
            >
              Proceed to checkout
            </button>
          </div>
        ) : (
          <Link
            href="/checkout"
            className="mt-5 flex h-12 w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#087DB5] to-[#073B70] text-sm font-bold text-white shadow-sm transition hover:brightness-110 active:scale-[0.99]"
          >
            Proceed to checkout
          </Link>
        )}

        <div className="mt-4 rounded-xl border border-teal-200/70 bg-teal-50/40 p-3 text-xs text-slate-600 dark:border-teal-500/20 dark:bg-slate-800/60 dark:text-slate-300 space-y-1.5">
          <div className="flex items-center gap-1.5 font-bold text-[#073B70] dark:text-white">
            <ShieldCheck className="h-4 w-4 text-[#00B7B0]" />
            <span>MOVA Safe & Guaranteed</span>
          </div>
          <p className="text-[11px] leading-relaxed">
            Quality verified. Fast delivery across Ethiopia. Cash on delivery supported.
          </p>
        </div>
      </aside>

      {/* Sticky Mobile Bottom Checkout Bar */}
      {validItems.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-50 sm:hidden border-t border-slate-200/90 bg-white/95 px-4 py-2.5 shadow-2xl backdrop-blur-md dark:border-white/10 dark:bg-slate-900/95 pb-[max(0.625rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Total ({validItems.reduce((n, i) => n + i.quantity, 0)}):</span>
              <div className="text-lg font-bold text-slate-950 dark:text-white leading-tight">
                {formatPrice(total)}
              </div>
            </div>
            {hasOutOfStockItems ? (
              <button
                type="button"
                disabled
                className="flex-1 max-w-[200px] h-11 flex items-center justify-center rounded-xl bg-slate-200 text-xs font-bold text-slate-400 dark:bg-white/10 dark:text-slate-500 cursor-not-allowed"
              >
                Stock Issue
              </button>
            ) : (
              <Link
                href="/checkout"
                className="flex-1 max-w-[220px] h-11 flex items-center justify-center rounded-xl bg-gradient-to-r from-[#087DB5] to-[#073B70] text-sm font-bold text-white shadow-md active:scale-[0.99] transition"
              >
                Proceed to Checkout
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
